// layers/fetchLayers.us.ts
//
// Requêtes CARTO pour la section "US RAA" — reproduit le filtre fourni
// par l'utilisateur, contre le schéma RÉEL confirmé de la table :
//
//   SELECT * FROM `carto-dw-ac-kc6q03le.shared_us.us_hex_table_2025_05_filtered`
//   WHERE tokeep = "true"
//   AND airport_city_catchment IN (...)
//   AND o_metropolitan_area_clean IN (...)
//   AND total_weekly_trips > 1
//   AND distance_airport_h3_destination_nm >= ...
//   AND distance_airport_h3_destination_nm <= ...
//
// Les listes "Origin Airport" et "Metro Areas" sont chargées EN DIRECT
// depuis la table (SELECT DISTINCT), pas depuis une liste statique côté
// client — le format réel des valeurs de airport_city_catchment n'étant
// pas garanti (le param CARTO Builder s'appelle {{airport_city_state_iata}},
// ce qui suggère un format composite "Ville, État (IATA)" plutôt que le
// simple nom de ville qu'on devinait avant). En interrogeant la table
// directement, on n'a plus besoin de deviner ce format : on renvoie
// exactement ce qu'elle contient.

import {
  CARTO_API,
  CARTO_TOKEN,
  CARTO_API_ROUTES,
  CARTO_TOKEN_ROUTES,
  TABLE_US_HEX,
  TABLE_US_ROUTES,
  TABLE_FOCUS_CITIES,
  CARTO_API_FOCUS,
  CARTO_TOKEN_FOCUS,
} from "../config/carto.us";

/**
 * `conn` permet de cibler une connexion CARTO différente de la connexion
 * par défaut (CARTO_API/CARTO_TOKEN) — utilisé par les requêtes sur la
 * table routes (projet BigQuery "atr"), qui vit sous une connexion CARTO
 * séparée de celle des autres tables ("carto-dw-ac-kc6q03le").
 */
async function query(q: string, conn?: { api: string; token: string }) {
  const api = conn?.api ?? CARTO_API;
  const token = conn?.token ?? CARTO_TOKEN;
  const res = await fetch(api, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ q }),
  });
  const data = await res.json();
  if (!res.ok) {
    const detail = data.error || data.message || JSON.stringify(data);
    throw new Error(`CARTO ${res.status}: ${detail}`);
  }
  return data;
}

const ROUTES_CONN = { api: CARTO_API_ROUTES, token: CARTO_TOKEN_ROUTES };
const FOCUS_CONN = { api: CARTO_API_FOCUS, token: CARTO_TOKEN_FOCUS };

function sqlList(values: string[]) {
  return values
    .filter((v) => typeof v === "string" && v.trim().length > 0)
    .map((v) => `'${v.replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`)
    .join(", ");
}

export interface USHexRow {
  h3_destination?: string;
  h3_cent_latitude?: string;
  h3_cent_longitude?: string;
  h3_city_dest?: string;
  h3_state_code_dest?: string;
  airport_city_catchment?: string;
  o_metropolitan_area_clean?: string;
  total_weekly_trips?: number;
  distance_airport_h3_destination_nm?: number;
  tokeep?: string;
  [key: string]: any;
}

export interface USHexFilters {
  catchments: string[]; // valeurs RÉELLES de airport_city_catchment (chargées depuis la table)
  metroAreas: string[]; // valeurs RÉELLES de o_metropolitan_area_clean
  gcdNmFrom: number;
  gcdNmTo: number;
}

/**
 * Reproduit le WHERE clause fourni par l'utilisateur, contre le schéma réel.
 * Si `metroAreas` est vide, le filtre o_metropolitan_area_clean est omis
 * (équivalent à "toutes les métros") plutôt que de générer un IN () vide.
 */
function buildWhere(filters: USHexFilters): string {
  const { catchments, metroAreas, gcdNmFrom, gcdNmTo } = filters;
  const parts = [
    `tokeep = "true"`,
    `airport_city_catchment IN (${sqlList(catchments)})`,
  ];
  if (metroAreas.length > 0) {
    parts.push(`o_metropolitan_area_clean IN (${sqlList(metroAreas)})`);
  }
  parts.push(`total_weekly_trips > 1`);
  parts.push(`distance_airport_h3_destination_nm >= ${gcdNmFrom}`);
  parts.push(`distance_airport_h3_destination_nm <= ${gcdNmTo}`);
  return parts.join("\n    AND ");
}

/** Lignes brutes (une par h3/catchment/destination) — utilisé par le dashboard filtré. */
export async function fetchUSHexData(filters: USHexFilters): Promise<USHexRow[]> {
  if (filters.catchments.length === 0) return [];
  const sql = `
    SELECT *
    FROM \`${TABLE_US_HEX}\`
    WHERE ${buildWhere(filters)}
  `;
  const data = await query(sql);
  return data.rows || [];
}

export interface USMobilityHexPoint {
  h3_destination: string;
  trips_weekly: number;
  city: string;
  state: string;
  cent_lat: number;
  cent_lng: number;
}

/**
 * Agrégation par hexagone — un enregistrement par h3_destination, hauteur
 * ET couleur de la barre 3D basées uniquement sur SUM(total_weekly_trips)
 * (métrique unique demandée, plus de mélange avec un champ "calibré").
 */
export async function fetchUSMobilityHex(filters: USHexFilters): Promise<USMobilityHexPoint[]> {
  if (filters.catchments.length === 0) return [];
  const sql = `
    SELECT
      h3_destination,
      SUM(total_weekly_trips) AS trips_weekly,
      MAX(h3_city_dest) AS city,
      MAX(h3_state_code_dest) AS state,
      MAX(CAST(h3_cent_latitude AS FLOAT64)) AS cent_lat,
      MAX(CAST(h3_cent_longitude AS FLOAT64)) AS cent_lng
    FROM \`${TABLE_US_HEX}\`
    WHERE ${buildWhere(filters)}
    GROUP BY h3_destination
  `;
  const data = await query(sql);
  return (data.rows || []).map((r: any) => ({
    h3_destination: r.h3_destination,
    trips_weekly: Number(r.trips_weekly) || 0,
    city: r.city || "",
    state: r.state || "",
    cent_lat: Number(r.cent_lat) || 0,
    cent_lng: Number(r.cent_lng) || 0,
  }));
}

/**
 * Liste distincte des ORIGIN AIRPORTS (airport_city_catchment) disponibles
 * — chargée en direct depuis la table, pas devinée côté client. Peuple le
 * multi-select "Origin Airport (City, State)".
 */
export async function fetchUSCatchments(): Promise<string[]> {
  const sql = `
    SELECT DISTINCT airport_city_catchment
    FROM \`${TABLE_US_HEX}\`
    WHERE tokeep = "true"
    AND airport_city_catchment IS NOT NULL
    ORDER BY airport_city_catchment
  `;
  const data = await query(sql);
  return (data.rows || []).map((r: any) => r.airport_city_catchment).filter(Boolean);
}

/** Liste distincte des métros disponibles pour un ensemble de catchments. */
export async function fetchUSMetroAreas(catchments: string[]): Promise<string[]> {
  if (catchments.length === 0) return [];
  const sql = `
    SELECT DISTINCT o_metropolitan_area_clean
    FROM \`${TABLE_US_HEX}\`
    WHERE tokeep = "true"
    AND airport_city_catchment IN (${sqlList(catchments)})
    AND o_metropolitan_area_clean IS NOT NULL
    ORDER BY o_metropolitan_area_clean
  `;
  const data = await query(sql);
  return (data.rows || []).map((r: any) => r.o_metropolitan_area_clean).filter(Boolean);
}

/** Extrait l'index H3 d'une ligne — conservé pour compat avec DashboardUS.tsx. */
export function getH3Index(row: USHexRow): string | null {
  return row.h3_destination || null;
}

// ============================================================
// Table "us_routes_table_evolution" — évolution du réseau de routes US
// (fermetures depuis 2000). Reproduit exactement le filtre CARTO Builder :
//
//   SELECT * FROM `atr.usa.us_routes_table_evolution_oag2026_07`
//   WHERE distance_nm >= {{distance_from}}
//   AND distance_nm <= {{distance_to}}
//   AND year_ref = {{year_slider}}
//   AND route_type IN {{route_status}}
// ============================================================

export interface USRouteRow {
  route_type?: string;
  distance_nm?: number;
  year_ref?: number;
  // Le nom exact de la colonne géométrie n'a pas été confirmé — on lit
  // défensivement les alias les plus probables via getRouteGeometry().
  geom?: any;
  geometry?: any;
  the_geom?: any;
  origin_iata?: string;
  dest_iata?: string;
  [key: string]: any;
}

export interface USRouteFilters {
  distanceFrom: number;
  distanceTo: number;
  year: number;
  routeStatus: string[]; // valeurs réelles de route_type
}

export async function fetchUSRoutes(filters: USRouteFilters): Promise<USRouteRow[]> {
  const { distanceFrom, distanceTo, year, routeStatus } = filters;
  if (routeStatus.length === 0) return [];
  const sql = `
    SELECT *
    FROM ${TABLE_US_ROUTES}
    WHERE distance_nm >= ${distanceFrom}
    AND distance_nm <= ${distanceTo}
    AND year_ref = ${year}
    AND route_type IN (${sqlList(routeStatus)})
  `;
  const data = await query(sql, ROUTES_CONN);
  return data.rows || [];
}

/** Liste distincte des statuts de route (route_type) — chargée en direct. */
export async function fetchUSRouteStatuses(): Promise<string[]> {
  const sql = `
    SELECT DISTINCT route_type
    FROM ${TABLE_US_ROUTES}
    WHERE route_type IS NOT NULL
    ORDER BY route_type
  `;
  const data = await query(sql, ROUTES_CONN);
  return (data.rows || []).map((r: any) => r.route_type).filter(Boolean);
}

/**
 * Années réellement disponibles dans year_ref — chargées en direct plutôt
 * que devinées (ROUTES_YEAR_MIN/MAX/DEFAULT dans carto.us.ts n'étaient
 * qu'une estimation ; si year_ref n'a par ex. que des valeurs par pas de
 * 5 ans, ou si 2024 n'existe pas dans la table, le slider par défaut
 * tombait sur une année vide et fetchUSRoutes() renvoyait silencieusement
 * 0 ligne à chaque fois).
 */
export async function fetchUSRouteYears(): Promise<number[]> {
  const sql = `
    SELECT DISTINCT year_ref
    FROM ${TABLE_US_ROUTES}
    WHERE year_ref IS NOT NULL
    ORDER BY year_ref
  `;
  const data = await query(sql, ROUTES_CONN);
  return (data.rows || [])
    .map((r: any) => Number(r.year_ref))
    .filter((y: number) => !Number.isNaN(y));
}

/**
 * Extrait la géométrie d'une ligne quel que soit le nom réel de la colonne
 * (geom / geometry / the_geom), et quel que soit le format renvoyé par
 * l'API CARTO (objet GeoJSON déjà parsé, ou chaîne JSON à parser).
 */
/**
 * Parse un WKT LINESTRING / MULTILINESTRING en objet GeoJSON-like
 * (compatible avec L.geoJSON côté RoutesMapUS.tsx). Les colonnes
 * GEOGRAPHY de BigQuery/CARTO renvoient généralement du WKT texte
 * ("LINESTRING(-86.15 39.77, -79.99 40.44)"), PAS du JSON — l'ancienne
 * version de getRouteGeometry() ne tentait que JSON.parse(), qui échoue
 * silencieusement sur du WKT (exception attrapée -> retourne null),
 * ce qui explique qu'aucune route ne s'affichait.
 */
function parseWKT(wkt: string): any | null {
  const trimmed = wkt.trim();

  const lsMatch = trimmed.match(/^LINESTRING\s*\(([^)]+)\)$/i);
  if (lsMatch) {
    const coordinates = lsMatch[1].split(",").map((pair) => {
      const [lon, lat] = pair.trim().split(/\s+/).map(Number);
      return [lon, lat];
    });
    return { type: "LineString", coordinates };
  }

  const mlsMatch = trimmed.match(/^MULTILINESTRING\s*\((.+)\)$/i);
  if (mlsMatch) {
    const coordinates: number[][][] = [];
    const groupRe = /\(([^()]+)\)/g;
    let m: RegExpExecArray | null;
    while ((m = groupRe.exec(mlsMatch[1]))) {
      const line = m[1].split(",").map((pair) => {
        const [lon, lat] = pair.trim().split(/\s+/).map(Number);
        return [lon, lat];
      });
      coordinates.push(line);
    }
    return { type: "MultiLineString", coordinates };
  }

  return null;
}

/**
 * Extrait la géométrie d'une ligne quel que soit le nom réel de la colonne
 * (geom / geometry / Geometry / the_geom), et quel que soit le format
 * renvoyé par l'API CARTO : WKT texte (le cas normal pour une colonne
 * GEOGRAPHY BigQuery) ou JSON déjà parsé/à parser.
 */
export function getRouteGeometry(row: USRouteRow): any | null {
  // "line_geom" est le vrai nom de colonne dans cette table Databricks
  // (confirmé via le diagnostic en console) — les autres alias sont
  // gardés en secours au cas où d'autres tables routes suivraient un
  // nommage différent.
  const raw = row.line_geom ?? row.geom ?? row.geometry ?? row.Geometry ?? row.the_geom ?? null;
  if (!raw) return null;

  if (typeof raw === "string") {
    const upper = raw.trim().toUpperCase();
    if (upper.startsWith("LINESTRING") || upper.startsWith("MULTILINESTRING")) {
      return parseWKT(raw);
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  return raw;
}

export async function fetchFocusCities() {
  const sql = `
    SELECT *
    FROM ${TABLE_FOCUS_CITIES}
    WHERE focus_top15 = TRUE
  `;

  const data = await query(sql, FOCUS_CONN);
  return data.rows || [];
}